import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Calendar as CalIcon, Clock, CheckCircle2, IndianRupee, Shield, Sparkles } from "lucide-react";
import SEO from "../components/SEO";

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toYMD(d);
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", matter: "", time: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const dateOptions = useMemo(() => {
    const arr = [];
    const base = new Date();
    for (let i = 1; i <= 14; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      if (d.getDay() === 0) continue;
      arr.push(d);
    }
    return arr;
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    api
      .get(`/bookings/slots?date_str=${selectedDate}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.time) {
      toast.error("Please fill name, phone and select a time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        matter: form.matter || null,
        date: selectedDate,
        time: form.time,
      });
      setConfirmation(data);
      toast.success("Consultation booked & payment confirmed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="pt-[72px] min-h-[80vh] bg-gradient-to-b from-[#EFF6FF] to-white" data-testid="booking-confirmation">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full mx-auto flex items-center justify-center mb-6 shadow-xl shadow-emerald-200">
            <CheckCircle2 size={36} className="text-white" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-600 mb-3">Booking Confirmed</div>
          <h1 className="font-serif text-4xl text-[#0A2540] font-semibold">We will see you in chambers.</h1>
          <div className="mt-8 card-elevated p-8 text-left">
            <div className="grid grid-cols-2 gap-5 text-sm">
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Name</div><div className="text-[#0A2540] mt-1 font-medium">{confirmation.name}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Phone</div><div className="text-[#0A2540] mt-1 font-medium">{confirmation.phone}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Date</div><div className="text-[#0A2540] mt-1 font-medium">{confirmation.date}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Time</div><div className="text-[#0A2540] mt-1 font-medium">{confirmation.time}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Amount</div><div className="text-[#0A2540] mt-1 font-medium">₹{confirmation.amount}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Payment ID</div><div className="text-[#0A2540] mt-1 font-mono text-xs">{confirmation.payment_id}</div></div>
            </div>
          </div>
          <p className="mt-8 text-slate-500 text-sm">
            A call-back will be arranged. For urgent matters, please tap the WhatsApp button.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[72px] bg-slate-50/50" data-testid="booking-page">
      <SEO
        title="Book Paid Consultation — Advocate Shubham Makkar, Chandigarh"
        description="Book a 45-minute paid consultation (₹1,500) with Advocate Shubham Makkar — corporate lawyer in Chandigarh."
        keywords="book corporate lawyer chandigarh, paid legal consultation chandigarh"
      />

      <section className="relative bg-gradient-to-b from-[#EFF6FF] to-white overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm px-4 py-2 rounded-full mb-6">
            <Sparkles size={14} className="text-[#1D4ED8]" />
            <span className="text-xs tracking-wider text-[#1D4ED8] font-medium">
              Paid Consultation
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A2540] tracking-tight leading-[1.05] max-w-4xl">
            Book a 45-minute consultation with Advocate Shubham Makkar.
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-slate-600">
            <div className="flex items-center gap-2">
              <IndianRupee size={18} className="text-[#1D4ED8]" />
              <span className="font-serif text-2xl text-[#0A2540] font-semibold">1,500</span>
              <span className="text-xs text-slate-500 uppercase tracking-wider">per session</span>
            </div>
            <div className="text-slate-300">·</div>
            <div className="text-sm">45 minutes · In-person / Video / Phone</div>
            <div className="text-slate-300">·</div>
            <div className="flex items-center gap-2 text-sm">
              <Shield size={14} className="text-emerald-500" />
              100% confidential
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 pt-10">
          <div className="lg:col-span-7 space-y-6">
            <div className="card-elevated p-8">
              <div className="flex items-center gap-2 mb-5">
                <CalIcon size={18} className="text-[#1D4ED8]" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Select Date</span>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="date-grid">
                {dateOptions.map((d) => {
                  const ymd = toYMD(d);
                  const active = selectedDate === ymd;
                  return (
                    <button
                      key={ymd}
                      onClick={() => { setSelectedDate(ymd); setForm((f) => ({ ...f, time: "" })); }}
                      className={`flex flex-col items-center py-3 border rounded-xl transition-all ${active
                        ? "bg-gradient-to-br from-[#1D4ED8] to-[#0A2540] text-white border-[#1D4ED8] shadow-md shadow-blue-200 scale-[1.03]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#1D4ED8] hover:shadow-sm"
                        }`}
                      data-testid={`date-${ymd}`}
                    >
                      <span className={`text-[10px] uppercase tracking-widest font-medium ${active ? "text-blue-100" : "text-slate-400"}`}>
                        {d.toLocaleDateString("en-IN", { weekday: "short" })}
                      </span>
                      <span className="font-serif text-xl leading-none mt-1 font-semibold">{d.getDate()}</span>
                      <span className={`text-[10px] uppercase tracking-widest mt-1 font-medium ${active ? "text-blue-100" : "text-slate-400"}`}>
                        {d.toLocaleDateString("en-IN", { month: "short" })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="card-elevated p-8">
              <div className="flex items-center gap-2 mb-5">
                <Clock size={18} className="text-[#1D4ED8]" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Select Time</span>
              </div>
              {loadingSlots && <div className="text-slate-500 text-sm">Loading slots…</div>}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="slots-grid">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    disabled={!s.available}
                    onClick={() => setForm((f) => ({ ...f, time: s.time }))}
                    className={`py-3 border rounded-xl transition-all text-sm font-medium ${form.time === s.time
                      ? "bg-gradient-to-br from-[#1D4ED8] to-[#0A2540] text-white border-[#1D4ED8] shadow-md shadow-blue-200"
                      : s.available
                        ? "bg-white text-slate-700 border-slate-200 hover:border-[#1D4ED8] hover:shadow-sm"
                        : "bg-slate-50 text-slate-300 border-slate-100 line-through cursor-not-allowed"
                      }`}
                    data-testid={`slot-${s.time}`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-5 card-elevated p-8 space-y-4 h-fit sticky top-[90px]" data-testid="booking-form">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1D4ED8] mb-2">Your Details</div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Full Name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 px-3.5 py-3 text-slate-800 placeholder:text-slate-400 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/15 rounded-lg text-sm transition-all"
                placeholder="e.g. Ranjit Kaur"
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Phone *</label>
              <input
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 px-3.5 py-3 text-slate-800 placeholder:text-slate-400 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/15 rounded-lg text-sm transition-all"
                placeholder="+91 98XXXXXXXX"
                data-testid="input-phone"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 px-3.5 py-3 text-slate-800 placeholder:text-slate-400 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/15 rounded-lg text-sm transition-all"
                placeholder="name@example.com"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Brief on Matter</label>
              <textarea
                rows={4}
                value={form.matter}
                onChange={(e) => setForm({ ...form, matter: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 px-3.5 py-3 text-slate-800 placeholder:text-slate-400 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/15 rounded-lg text-sm transition-all"
                placeholder="FIR number, section, brief facts…"
                data-testid="input-matter"
              />
            </div>

            <div className="py-4 border-y border-slate-100 flex items-center justify-between">
              <div className="text-sm text-slate-500">Consultation Fee</div>
              <div className="font-serif text-3xl text-[#0A2540] font-semibold">₹1,500</div>
            </div>

            <button
              type="submit"
              disabled={submitting || !form.time}
              className="w-full primary-btn justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-booking"
            >
              {submitting ? "Confirming…" : "Confirm & Pay ₹1,500"}
            </button>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Payment in this demo is processed via mock gateway. Razorpay integration can be
              enabled with live keys — signatures are HMAC-SHA256 verified server-side.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
